import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useFlash } from "@/hooks/productTimerHooks";
import {
  Check,
  Copy,
  Database,
  Hash,
  Layers,
  Sigma,
  Table2,
  type LucideIcon,
} from "lucide-react";
import { RootState } from "@/redux/store";
import { EM_DASH, Stat, StatStrip } from "@/components/FpPages/RMS/design";
import { KeyValue, Overline } from "@/components/FpPages/RMS/design/Card";
import ArtifactPaneHeader, { type PaneAction } from "./ArtifactPaneHeader";
import {
  getIodlSourceData,
  type IodlSourceData,
} from "./services/getIodlSourceData";

/**
 * Viewer for a citation drawn from a structured data source (an IODL data
 * set, a client's own database feed): the pane a citation chip opens in the
 * same drawer an RMS record or an artifact opens in, wearing the same header.
 *
 * What the citation carries is one row of values in a column order — a
 * search row, a `group_by` bucket, a whole-set aggregate or a row count — and
 * the viewer reads the shape off the columns rather than showing every one
 * as a one-row table: a row reads as a record, an aggregate as figures with
 * what they are over, a bucket as the record it groups by plus its figures.
 * The source's own name is the pane's title; the query, for the sources that
 * expose one, sits under the values with a Copy action in the header.
 *
 * The legacy path (a persisted query answer's `qid`) still lazy-loads the
 * full result table from getQueryAnswerIodlData and shows it as a table.
 */

type CellValue = string | number | null;
type Row = CellValue[];

/** The metric keys the backend's aggregate and `group_by` shapes carry, in
 *  the order a reader wants them: the figure most often cited first. */
const METRIC_ORDER = ["sum", "avg", "min", "max", "count"] as const;
type MetricKey = (typeof METRIC_ORDER)[number];
const METRIC_LABEL: Record<MetricKey, string> = {
  sum: "Sum",
  avg: "Average",
  min: "Minimum",
  max: "Maximum",
  count: "Count",
};
const isMetricKey = (name: string): name is MetricKey =>
  (METRIC_ORDER as readonly string[]).includes(name);

interface Field {
  name: string;
  value: CellValue;
}

type CitedShape =
  | { kind: "count"; count: CellValue }
  | { kind: "aggregate"; column: string; metrics: Field[] }
  | { kind: "group"; keys: Field[]; metrics: Field[] }
  | { kind: "row"; fields: Field[] }
  | { kind: "table" };

const zip = (columns: string[], row: Row): Field[] =>
  columns.map((name, i) => ({ name, value: row[i] ?? null }));

const orderMetrics = (fields: Field[]): Field[] =>
  METRIC_ORDER.flatMap((key) => fields.filter((f) => f.name === key));

/** Read the citation's shape off its column order (the payload carries no
 *  discriminator): `total_count` alone is a count; `column` followed by
 *  metrics is a whole-set aggregate; grouping keys followed by metrics is a
 *  bucket; anything else is a row. */
export const citedShape = (
  columns: string[],
  rows: Row[],
  totalRows: number
): CitedShape => {
  // The legacy path knows its population before its rows arrive.
  if (rows.length > 1 || totalRows > 1) return { kind: "table" };
  const row = rows[0] ?? [];
  const fields = zip(columns, row);
  if (columns.length === 1 && columns[0] === "total_count") {
    return { kind: "count", count: row[0] ?? null };
  }
  const countAt = columns.indexOf("count");
  if (countAt > 0 && columns.slice(countAt).every(isMetricKey)) {
    const metrics = orderMetrics(fields.slice(countAt));
    if (columns[0] === "column" && countAt === 1) {
      return {
        kind: "aggregate",
        column: String(row[0] ?? ""),
        metrics,
      };
    }
    return { kind: "group", keys: fields.slice(0, countAt), metrics };
  }
  return { kind: "row", fields };
};

/** A column name as a label: underscores and camelCase become words. */
export const columnLabel = (name: string): string =>
  name
    .replace(/_+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim();

const capitalize = (text: string): string =>
  text.charAt(0).toUpperCase() + text.slice(1);

const grouped = new Intl.NumberFormat("en-US", { maximumFractionDigits: 4 });

/** A figure in a stat card: grouped, at most four decimals. */
const formatFigure = (value: CellValue): string => {
  if (value === null || value === "") return EM_DASH;
  if (typeof value === "number") return grouped.format(value);
  return value;
};

/** A field's value in a record. Integers stay as written (an account
 *  number, an id, a year), so only amounts with a fractional part group. */
const formatCell = (value: CellValue): string => {
  if (value === null || value === "") return EM_DASH;
  if (typeof value === "number") {
    return Number.isInteger(value) ? String(value) : grouped.format(value);
  }
  return value;
};

const looksLikeCode = (value: string): boolean =>
  /^-?[\d.,]+$/.test(value) || /^\d{4}-\d{2}-\d{2}/.test(value);

/** The record's own name, when one of its values reads as one: the first
 *  text value that is not a number or a date. */
const recordTitle = (fields: Field[]): string | null => {
  const named = fields.find(
    (f) =>
      typeof f.value === "string" &&
      f.value.trim() !== "" &&
      !looksLikeCode(f.value.trim())
  );
  return named ? String(named.value) : null;
};

const plural = (n: number, noun: string): string =>
  `${grouped.format(n)} ${n === 1 ? noun : `${noun}s`}`;

const overlineStyle: React.CSSProperties = {
  fontFamily: "var(--fp-font-ui)",
  fontSize: "var(--fp-t-micro)",
  fontWeight: "var(--fp-w-head)" as unknown as number,
  letterSpacing: "var(--fp-track-caps)",
  textTransform: "uppercase",
  color: "var(--fp-ink-3)",
};

/** The pane's lede: an icon tile, the cited thing's name and what kind of
 *  thing it is. Mirrors the record preview's name-and-kind block. */
const Lede: React.FC<{ icon: LucideIcon; title: string; meta: string }> = ({
  icon: Icon,
  title,
  meta,
}) => (
  <div className="flex flex-shrink-0 items-center gap-3 border-b border-gray-100 px-4 pb-3.5 pt-4">
    <div className="flex h-10 w-10 flex-none items-center justify-center rounded-[10px] bg-[#ECEDEF] text-[#5a6068]">
      <Icon className="h-[18px] w-[18px]" strokeWidth={1.8} />
    </div>
    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
      <div
        className="truncate"
        style={{
          fontFamily: "var(--fp-font-ui)",
          fontSize: "var(--fp-t-lede)",
          fontWeight: "var(--fp-w-head)" as unknown as number,
          letterSpacing: "var(--fp-track-head)",
          color: "var(--fp-ink)",
        }}
      >
        {title}
      </div>
      <div style={overlineStyle}>{meta}</div>
    </div>
  </div>
);

const Fields: React.FC<{ fields: Field[] }> = ({ fields }) => (
  <dl className="m-0">
    {fields.map((field) => (
      <KeyValue key={field.name} label={columnLabel(field.name)}>
        <span
          style={{
            overflowWrap: "anywhere",
            fontFeatureSettings:
              typeof field.value === "number"
                ? "var(--fp-feat-num)"
                : undefined,
            color: field.value === null ? "var(--fp-ink-4)" : undefined,
          }}
        >
          {formatCell(field.value)}
        </span>
      </KeyValue>
    ))}
  </dl>
);

const Figures: React.FC<{ metrics: Field[] }> = ({ metrics }) => (
  <StatStrip>
    {metrics.map((metric) => (
      <Stat
        key={metric.name}
        label={METRIC_LABEL[metric.name as MetricKey] ?? metric.name}
        value={formatFigure(metric.value)}
      />
    ))}
  </StatStrip>
);

const countOf = (metrics: Field[]): number | null => {
  const count = metrics.find((m) => m.name === "count")?.value;
  return typeof count === "number" ? count : null;
};

const IodlSourceViewer: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const {
    sql,
    sourceLabel,
    iodlColumns,
    rowCount,
    iodlReasoning,
    iodlQid,
    iodlRows,
  } = useSelector((state: RootState) => state.fileViewerData) as {
    sql: string | null;
    sourceLabel: string | null;
    iodlColumns: string[] | null;
    rowCount: number | null;
    iodlReasoning: string | null;
    iodlQid: string | null;
    iodlRows: Row[] | null;
  };

  const [data, setData] = useState<IodlSourceData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [copied, triggerCopied] = useFlash(2000);

  useEffect(() => {
    // Agentic citations carry their single cited row inline (iodlRows), so there
    // is nothing to fetch; only the legacy qid path lazy-loads the full table.
    if (!iodlQid || (iodlRows && iodlRows.length)) return;
    let cancelled = false;
    setLoading(true);
    setError(false);
    getIodlSourceData(iodlQid)
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [iodlQid, iodlRows]);

  // Prefer the eagerly-seeded metadata (instant), fall back to the fetched
  // payload (e.g. when the viewer is opened cold without a click).
  const displaySql = sql || data?.sql || "";
  const columns = iodlColumns || data?.columns || [];
  // Rows come from the fetch (legacy full table) or, for agentic citations, the
  // single cited row seeded inline. (#10389)
  const rows: Row[] = data?.rows?.length ? data.rows : (iodlRows ?? []);
  const totalRows = rowCount ?? rows.length;
  const label = sourceLabel ?? "IODL";
  const shape = citedShape(columns, rows, totalRows);

  const handleCopy = () => {
    if (!displaySql) return;
    navigator.clipboard.writeText(displaySql).then(() => triggerCopied());
  };

  // The query is shown only for the sources that expose one. A source an
  // organization owns does not (#11103): the reader is looking at their own
  // book, and the tables it is stored in explain nothing. Gating on the SQL's
  // presence keeps the section and the Copy action from drifting apart.
  const actions: PaneAction[] = displaySql
    ? [
        {
          key: "copy-sql",
          label: "Copy SQL",
          hint: copied ? "Copied!" : "Copy SQL",
          icon: copied ? Check : Copy,
          onSelect: handleCopy,
        },
      ]
    : [];

  const lede = (() => {
    switch (shape.kind) {
      case "count":
        return <Lede icon={Hash} title="All matching rows" meta="Row count" />;
      case "aggregate": {
        const over = countOf(shape.metrics);
        return (
          <Lede
            icon={Sigma}
            title={capitalize(columnLabel(shape.column)) || "Aggregate"}
            meta={
              over === null
                ? "Aggregate"
                : `Aggregate · over ${plural(over, "row")}`
            }
          />
        );
      }
      case "group": {
        const size = countOf(shape.metrics);
        const name = shape.keys
          .map((k) => formatCell(k.value))
          .filter((v) => v !== EM_DASH)
          .join(" · ");
        return (
          <Lede
            icon={Layers}
            title={name || "Group"}
            meta={size === null ? "Group" : `Group · ${plural(size, "row")}`}
          />
        );
      }
      case "row": {
        const name = recordTitle(shape.fields);
        return (
          <Lede
            icon={Database}
            title={name ?? "Row"}
            meta={
              name === null
                ? plural(shape.fields.length, "field")
                : `Row · ${plural(shape.fields.length, "field")}`
            }
          />
        );
      }
      case "table":
        return (
          <Lede
            icon={Table2}
            title="Result set"
            meta={`${plural(totalRows, "row")} · ${plural(columns.length, "column")}`}
          />
        );
      default: {
        const unhandled: never = shape;
        throw new Error(`unhandled shape: ${String(unhandled)}`);
      }
    }
  })();

  const body = (() => {
    if (loading) {
      return (
        <div className="space-y-2 pt-3" aria-busy="true">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-4 animate-pulse rounded bg-gray-200" />
          ))}
        </div>
      );
    }
    if (error) {
      return (
        <p
          className="pt-3"
          style={{ fontSize: "var(--fp-t-body)", color: "var(--fp-ink-3)" }}
        >
          Failed to load {label} data
        </p>
      );
    }
    if (rows.length === 0) {
      return (
        <p
          className="pt-3"
          style={{ fontSize: "var(--fp-t-body)", color: "var(--fp-ink-3)" }}
        >
          No rows returned
        </p>
      );
    }
    switch (shape.kind) {
      case "count":
        return (
          <div className="pt-4">
            <StatStrip>
              <Stat label="Count" value={formatFigure(shape.count)} />
            </StatStrip>
          </div>
        );
      case "aggregate":
        return (
          <div className="pt-4">
            <Figures metrics={shape.metrics} />
          </div>
        );
      case "group":
        return (
          <div className="flex flex-col gap-4">
            <Fields fields={shape.keys} />
            <Figures metrics={shape.metrics} />
          </div>
        );
      case "row":
        return <Fields fields={shape.fields} />;
      case "table":
        return (
          <div
            className="mt-4 overflow-hidden"
            style={{
              background: "var(--fp-surface)",
              border: "1px solid var(--fp-border)",
              borderRadius: "var(--fp-card-r)",
              boxShadow: "var(--fp-lift)",
            }}
          >
            <div className="overflow-x-auto">
              <table
                className="min-w-full border-collapse"
                style={{
                  fontFamily: "var(--fp-font-ui)",
                  fontSize: "var(--fp-t-body)",
                }}
              >
                <thead>
                  <tr style={{ background: "var(--fp-band)" }}>
                    {columns.map((col) => (
                      <th
                        key={col}
                        className="whitespace-nowrap px-3 text-left"
                        style={{
                          ...overlineStyle,
                          color: "var(--fp-ink-4)",
                          height: "var(--fp-thead-h)",
                          boxShadow: "inset 0 -1px 0 0 var(--fp-border)",
                        }}
                      >
                        {columnLabel(col)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, ri) => (
                    <tr key={ri}>
                      {row.map((cell, ci) => (
                        <td
                          key={ci}
                          className="whitespace-nowrap px-3"
                          style={{
                            height: "var(--fp-row-h)",
                            color:
                              cell === null
                                ? "var(--fp-ink-4)"
                                : "var(--fp-ink)",
                            fontFeatureSettings:
                              typeof cell === "number"
                                ? "var(--fp-feat-num)"
                                : undefined,
                            boxShadow:
                              ri === rows.length - 1
                                ? undefined
                                : "inset 0 -1px 0 0 var(--fp-border)",
                          }}
                        >
                          {formatCell(cell)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      default: {
        const unhandled: never = shape;
        throw new Error(`unhandled shape: ${String(unhandled)}`);
      }
    }
  })();

  return (
    <div className="flex h-full flex-col">
      <ArtifactPaneHeader title={label} actions={actions} onClose={onClose} />
      {lede}
      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4 pt-2">
        {body}
        {displaySql && (
          <section className="mt-5">
            <Overline>Query</Overline>
            <pre
              className="m-0 whitespace-pre-wrap break-words px-3.5 py-3"
              style={{
                background: "var(--fp-bg-well)",
                borderRadius: "10px",
                fontFamily: "var(--fp-font-mono)",
                fontSize: "var(--fp-t-fine)",
                lineHeight: 1.55,
                color: "var(--fp-ink)",
                overflowWrap: "anywhere",
              }}
            >
              {displaySql}
            </pre>
            {iodlReasoning && (
              <p
                className="mt-2"
                style={{
                  fontFamily: "var(--fp-font-ui)",
                  fontSize: "var(--fp-t-meta)",
                  lineHeight: "var(--fp-lh-body)",
                  color: "var(--fp-ink-3)",
                }}
              >
                {iodlReasoning}
              </p>
            )}
          </section>
        )}
      </div>
    </div>
  );
};

export default IodlSourceViewer;
